import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type UserRole = 'ADMIN' | 'CUSTOMER';

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  phone?: string | null;
  role: UserRole;
  /** Signed in with a temporary app password — must set their own before using the workspace */
  mustChangePassword?: boolean;
};

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const TOKEN_KEY = 'micus_musb_token';
const USER_KEY = 'micus_musb_user';

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    const user = raw ? (JSON.parse(raw) as AuthUser) : null;
    if (user && user.role !== 'CUSTOMER') {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

const storedUser = loadUser();

const initialState: AuthState = {
  user: storedUser,
  token: storedUser ? localStorage.getItem(TOKEN_KEY) : null,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginRequest(
      state,
      _action: PayloadAction<{ email: string; password: string }>,
    ) {
      state.loading = true;
      state.error = null;
    },
    loginSuccess(state, action: PayloadAction<{ token: string; user: AuthUser }>) {
      state.loading = false;
      state.error = null;
      state.token = action.payload.token;
      state.user = action.payload.user;
      localStorage.setItem(TOKEN_KEY, action.payload.token);
      localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user));
    },
    loginFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    logout(state) {
      state.user = null;
      state.token = null;
      state.error = null;
      state.loading = false;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('micus_musb_company');
    },
    clearAuthError(state) {
      state.error = null;
    },
    passwordChanged(state) {
      if (!state.user) return;
      state.user = { ...state.user, mustChangePassword: false };
      localStorage.setItem(USER_KEY, JSON.stringify(state.user));
    },
    userUpdated(state, action: PayloadAction<Partial<AuthUser>>) {
      if (!state.user) return;
      state.user = { ...state.user, ...action.payload };
      localStorage.setItem(USER_KEY, JSON.stringify(state.user));
    },
  },
});

export const { loginRequest, loginSuccess, loginFailure, logout, clearAuthError, passwordChanged, userUpdated } =
  authSlice.actions;
export default authSlice.reducer;
